import React, { useCallback, useState, useContext, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { useAlerts, userAgent } from '@theneatcompany/nui'
import { load } from '@fingerprintjs/botd'
import { usePathname } from 'next/navigation'
import { useIntl } from 'react-intl'
import { useReCaptcha } from 'next-recaptcha-v3'

import rollbar, { sendError, sendWarning, trackSignUpStep, trackSignUpEvent, trackSignUpCompleted, trackSignUpStarted } from '../../utils/rollbar'
import { NEAT_HOME_TRIAL_SKU, UPSELL_PLAN, UPSELL_COUPON, RECAPTCHA_V3_ACTIONS } from '../../constants'
import { WEB_APP_URL, AUTH_URL } from '../../env'
import { handleVerifyRecaptchaV3 } from '../../utils/recaptcha'
import { getVariantCookies } from '../../lib/tracking/success'

import {
  resolveTransition, STEPS, PSEUDO_STATES, OUTCOMES, EXITS,
} from './signUpMachine/transitions'
import {
  DELAYED_REDIRECT, QUEUE_KINDS, TRACKERS, buildStepPayload, createCheckoutTelemetry,
  enterCheckout, outcomeEventFor, planTelemetry, schedulesDelayedRedirect, stepEventFor,
} from './signUpMachine/telemetry'
import {
  isLinkedAndPurchasable, isPurchasable, readIsLinkedToGoogle, validationHasNeatUserId,
} from './signUpMachine/guards'
import { nextUserAfterAuthenticate } from './signUpMachine/userState'
import {
  classifyOrderResponse, ORDER_OUTCOMES, ORDER_FAILURE_MESSAGE,
} from './signUpMachine/orderResponse'
import { useSiteConfig } from './SiteConfigProvider'

const SignUpContext = React.createContext()

/**
 * How each classified order failure is reported. An outcome the upstream or
 * this app is responsible for is an error; one the customer's own input or
 * session caused is a warning, because it is expected traffic rather than a
 * defect. `unrecognized` is absent deliberately: it carries its own message,
 * sent from the switch, so it is never reported twice.
 */
const ORDER_FAILURE_REPORTERS = {
  [ORDER_OUTCOMES.serverFailure]: sendError,
  [ORDER_OUTCOMES.errorGeneric]: sendError,
  [ORDER_OUTCOMES.statusGeneric]: sendError,
  [ORDER_OUTCOMES.validationFailed]: sendWarning,
  [ORDER_OUTCOMES.noCcTrialOffered]: sendWarning,
  [ORDER_OUTCOMES.sessionExpired]: sendWarning,
  [ORDER_OUTCOMES.errorSpecific]: sendWarning,
  [ORDER_OUTCOMES.statusSpecific]: sendWarning,
  [ORDER_OUTCOMES.tooManyCcAttempts]: sendWarning,
}

export const useSignUp = () => useContext(SignUpContext)

export const PAYMENT_METHOD_TYPES = {
  ccPayment: 'ccPayment',
  paypalPayment: 'paypalPayment',
}

/** The two answers `validateUser` gives its callers. */
const VALIDATION_KINDS = {
  valid: 'valid',
  requestFailed: 'requestFailed',
}

/** The two answers `handleAuth` gives its callers. */
const AUTH_KINDS = {
  answered: 'answered',
  requestFailed: 'requestFailed',
}

/**
 * Whether a body is a validate response at all.
 *
 * The route answers `res.status(400).json(error)` and an `Error` serialises to
 * `{}`, so an unchecked body reads as a validation with no status -- which is
 * what every caller treats as squidward's "this user may not order". Both
 * fields squidward always sends are required, and `status` is pinned to the
 * two values it may hold rather than to truthiness.
 */
const isRecognizedValidation = (body) => (
  !!body && typeof body === 'object' && !Array.isArray(body)
  && Number.isInteger(body.status) && body.status >= 0 && body.status <= 1
  && !!body.user && typeof body.user === 'object' && !Array.isArray(body.user)
  && typeof body.isLinkedToGoogle === 'boolean'
)

const getForceOrderRequestValidationErrorCode = () => {
  try {
    const score = localStorage.getItem('forceOrderRequestValidationErrorCode')
    return score
  } catch {
    return undefined
  }
}

const SignUpProvider = ({ children }) => {
  const [startedCheckout, setStartedCheckout] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [navigation, setNavigation] = useState({ steps: STEPS, currentStep: STEPS.authenticate })
  const [user, setUser] = useState({})
  const [plan, setPlan] = useState({})
  const [coupon, setCoupon] = useState(null)
  const [utm, setUtm] = useState({})
  const [billingData, setBillingData] = useState({})
  const [canPurchasePlan, setCanPurchasePlan] = useState(false)
  const [isLinkedToGoogle, setIsLinkedToGoogle] = useState(false)
  const [order, setOrder] = useState({})
  const [csrfToken, setCsrfToken] = useState(null)
  const [showTooManyAttempts, setShowTooManyAttempts] = useState(false)

  const [failedAuth, setFailedAuth] = useState(null)
  const [isOrderProcessing, setIsOrderProcessing] = useState(false)
  const [isOrderRequestProcessing, setIsOrderRequestProcessing] = useState(false)
  const [isPayPalOpen, setIsPayPalOpen] = useState(false)
  const [orderError, setOrderError] = useState()

  const [showNoCcTrial, setShowNoCcTrial] = useState(false)

  const [recurlyFormValidation, setRecurlyFormValidation] = useState({})
  const [recurlyToken, setRecurlyToken] = useState()
  const [recurlyErrors, setRecurlyErrors] = useState()

  const [formHolderHeight, setFormHolderHeight] = useState(0)
  const [formBoxHeight, setFormBoxHeight] = useState(0)

  const [isRequestingPasswordReset, setIsRequestingPasswordReset] = useState(false)

  const botdRef = useRef()
  const userValidation = useRef()
  // One checkout, one `Sign Up Started`, plus the events that checkout owes.
  // Held in a ref because a re-entry to `authenticate` must not re-emit, a
  // re-render must not reset the gate, and the queue is delivery machinery
  // nothing renders -- appending to it should not cost a render. One object,
  // one lifetime: `clearCheckout` resets the pair in one assignment.
  const checkoutTelemetry = useRef(createCheckoutTelemetry())
  const { executeRecaptcha, loaded } = useReCaptcha()

  const alerts = useAlerts()

  const pathname = usePathname()
  const { formatMessage } = useIntl()
  const {
    sources, defaultSku, planSelect, upsell, backupSkuMap,
  } = useSiteConfig()

  const {
    query: {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      sku = '',
      couponCode = '',
      exists = false,
      jwt = '',
      oauthToken = '',
      refreshJWT = '',
      jwtExpiresAt = '',
      refreshJWTExpiresAt = '',
    },
    isReady,
    push,
  } = useRouter()

  const handleExecuteRecaptcha = useCallback(async (action) => {
    if (!loaded) {
      sendWarning(null, `SignUpProvider: executeRecaptcha not available: ${action}`)
      return undefined
    }
    const token = await executeRecaptcha(action)
    return token
  }, [executeRecaptcha, loaded])

  const clearCheckout = () => {
    alerts.clear()

    setNavigation({
      steps: STEPS,
      currentStep: STEPS[resolveTransition(PSEUDO_STATES.global, OUTCOMES.checkoutAbandoned, {})],
    })
    setUser({})
    setPlan({})
    setCoupon({})
    setCanPurchasePlan(false)
    setIsLinkedToGoogle(false)
    setOrder({})
    setCsrfToken(null)
    setBillingData({})
    setFailedAuth(null)
    setIsOrderProcessing(false)
    setIsPayPalOpen(false)
    setOrderError(null)
    setRecurlyFormValidation({})
    setRecurlyToken(null)
    setRecurlyErrors(null)

    userValidation.current = null
    // Resets the `Sign Up Started` gate AND drops any events the abandoned
    // checkout still owed. An event queued while its plan never loaded is
    // waiting on a `plan?.sku` that will now belong to a different checkout;
    // delivering it there would attribute it to the wrong sku, coupon and utm.
    checkoutTelemetry.current = createCheckoutTelemetry()
  }

  useEffect(() => {
    if (pathname !== '/t' && pathname !== '/t/success' && startedCheckout) {
      clearCheckout()
    }
    setStartedCheckout(pathname === '/t')
  }, [pathname])

  const fetchPlan = async (planLookup) => {
    try {
      const res = await fetch('/api/signup/plan', {
        method: 'POST',
        body: JSON.stringify({ plan: planLookup }),
      })

      const planRes = await res.json()

      // The route sends a failed lookup as 400 with the serialised error, `{}`,
      // and a squidward error body without `errors` arrives as 200; `sku` is
      // the plan's key, so a body without one is not a plan.
      if (!res.ok || !planRes?.sku) {
        throw Error('Plan response not ok or missing sku')
      }
      setPlan(planRes)

      return planRes
    } catch (error) {
      alerts.error('Error Fetching Plan')
      sendError(error, `Error fetching /api/signup/plan: ${planLookup}`)
      return null
    }
  }

  const navigateToStep = (step) => {
    setNavigation({ ...navigation, ...{ currentStep: navigation.steps[step] } })
  }

  // Declared before applyTransition, which reads them; both are pure
  // derivations of state declared above.
  const shouldSkipPlanSelect = useMemo(() => !planSelect || !!(plan.isTrial || plan.sku === 'vault50' || plan.sku?.startsWith('neathome')), [plan, planSelect])

  const shouldShowUpsell = plan.sku === NEAT_HOME_TRIAL_SKU && upsell

  const getAuthCallbackUrl = () => encodeURIComponent(`${window.location.origin}${pathname}?sku=${sku}${couponCode ? `&couponCode=${couponCode}` : ''}${utm.source ? `&utm_source=${utm.source}` : ''}${utm.medium ? `&utm_medium=${utm.medium}` : ''}${utm.campaign ? `&utm_campaign=${utm.campaign}` : ''}&exists=true`)

  const getAuthRedirectUrl = () => `${AUTH_URL}?scope=NEAT_COM&callbackUrl=${getAuthCallbackUrl()}&tokenVersion=3`

  const appAuthReturnUrl = `${WEB_APP_URL}/auth-return?jwt=${jwt}&oauthToken=${oauthToken}&refreshJWT=${refreshJWT}&jwtExpiresAt=${jwtExpiresAt}&refreshJWTExpiresAt=${refreshJWTExpiresAt}`

  // The outcome-named events fired from inside a submit handler. These were
  // never part of the step effect and are unchanged: the caller passes the
  // user it just built, because that is the user the event is about.
  const sendRollbarStepData = (stepName, userData = null) => {
    trackSignUpStep(stepName, userData || user, buildStepPayload({
      plan, coupon, utm, testsShown: getVariantCookies(),
    }))
  }

  /**
   * Ask the transition table where this outcome leads, then perform it.
   *
   * The routing DECISION lives in signUpMachine; every side effect stays in
   * the handler that triggers it -- the exits below are the same calls, made
   * from the same handlers, in the same order relative to the surrounding
   * recaptcha / lead / rollbar work.
   *
   * `overrides` lets a caller route on data it holds locally rather than on
   * React state that has not re-rendered yet (`userData` inside
   * saveStepAuthenticate, `fetchedPlan` inside the bootstrap effect) -- the
   * same values those sites read today.
   */
  const applyTransition = (from, outcome, overrides = {}) => {
    const { effects = {} } = overrides
    const effectiveUser = overrides.user ?? user
    const effectivePlan = overrides.plan ?? plan
    const effectiveValidation = overrides.validation ?? {}
    const destination = resolveTransition(from, outcome, {
      user: effectiveUser,
      plan: effectivePlan,
      validation: effectiveValidation,
      jwt,
      order,
      shouldShowUpsell,
      shouldSkipPlanSelect,
      planSelectDisabled: !planSelect,
    })

    // No row answers for this pair, so no arrival is queued either.
    if (destination === null) {
      sendError(null, 'Sign Up: unmodelled transition', { from, outcome, step: navigation.currentStep })
      alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
      return destination
    }

    // Arrival, not render: recorded once per transition into the step, and
    // sent by the flush effect once the state it describes has settled.
    const stepEvent = stepEventFor(destination)
    if (stepEvent) checkoutTelemetry.current.queue.push({ kind: QUEUE_KINDS.step, name: stepEvent })

    // Some events belong to what happened rather than to where it landed. A
    // back press lands on `authenticate`, which emits no arrival event; without
    // this the one capability this flow gains would be invisible in the funnel.
    const outcomeEvent = outcomeEventFor(outcome)
    if (outcomeEvent) checkoutTelemetry.current.queue.push({ kind: QUEUE_KINDS.outcome, name: outcomeEvent })

    switch (destination) {
      case EXITS.authRedirect:
        window.location.href = getAuthRedirectUrl()
        break
      case EXITS.ssoUrl:
        // The url comes from the auth response, not from the table, so this
        // exit can be reached holding nothing.
        if (effects.ssoUrl) {
          window.location.assign(effects.ssoUrl)
        } else {
          sendError(null, 'Sign Up: ssoUrl exit without url', { from, outcome })
          alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
        }
        break
      case EXITS.webappGoogleLogin:
        window.location.assign(`${WEB_APP_URL}/login?g_token_id=${user.googleId}`)
        break
      case EXITS.successPage:
        push('/t/success')
        break
      case EXITS.appAuthReturn:
        window.location.assign(appAuthReturnUrl)
        break
      case EXITS.appUrl:
        window.location.assign(window.location.origin)
        break
      case EXITS.errorAlert:
        alerts.error(effects.errorMessage)
        break
      case EXITS.failedAuth:
        setFailedAuth(effects.failedAuthMessage)
        break
      case EXITS.noop:
        // The table routes this pair nowhere (the original chain had no final
        // else), but an authenticated customer must not be left in silence.
        if (outcome === OUTCOMES.authenticatedUnresolved) {
          sendError(null, 'Sign Up: reset password unresolved', {
            from,
            outcome,
            status: effectiveValidation.status,
            hasNeatUserId: !!effectiveValidation.user?.neatUserId,
          })
          alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
        }
        break
      default:
        if (destination) navigateToStep(navigation.steps[destination])
    }

    // The existing-session landing leaves for the app on a timer. It is an
    // effect of that transition rather than a row: it picks no destination,
    // it re-enters the table later through the redirect outcome.
    if (schedulesDelayedRedirect(from, outcome)) {
      setTimeout(() => {
        applyTransition(PSEUDO_STATES.global, OUTCOMES.redirectToApp, { effects: { ssoUrl: order?.ssoUrl } })
      }, DELAYED_REDIRECT.delayMs)
    }

    return destination
  }

  useEffect(() => {
    load()
      .then((botd) => {
        botdRef.current = botd
      })
      .catch((error) => {
        rollbar.error('Bot Detection failed to load', error)
      })
  }, [])

  const detectBot = async (email) => {
    if (!botdRef?.current) {
      rollbar.info(`Bot Detection service not available for ${email} at step: ${navigation.currentStep}`)
      return
    }
    try {
      const botDetection = await botdRef.current.detect()
      rollbar.info(`Bot Detection for ${email} at step: ${navigation.currentStep}: ${botDetection.bot} `)
    } catch (error) {
      rollbar.error(`Bot Detection failed for ${email}`, error)
    }
  }

  const bannerDescription = useMemo(() => coupon?.utmCampaigns?.find(
    campaign => campaign.name === utm?.campaign?.toLowerCase(),
  )?.description ?? null, [utm, coupon])

  useEffect(() => {
    const fetchCouponByCampaign = async (campaign) => {
      const res = await fetch('/api/signup/coupon/campaign', {
        method: 'POST',
        body: JSON.stringify({ campaign }),
      })

      const campaignCoupon = await res.json()
      if (res.status && res.status > 300) {
        alerts.error('Error Fetching Coupon')
        return null
      }

      setCoupon(campaignCoupon)
      return campaignCoupon
    }

    if (utmSource || utmMedium || utmCampaign) {
      setUtm({
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
      })
    }

    if (utmCampaign) {
      fetchCouponByCampaign(utmCampaign)
    }
  }, [utmSource, utmMedium, utmCampaign])

  const fetchCoupon = async (promoCode) => {
    try {
      const res = await fetch('/api/signup/coupon', {
        method: 'POST',
        body: JSON.stringify({ promoCode }),
      })

      const couponRes = await res.json()
      if (res.status > 300 || couponRes.status > 300) {
        throw Error('The promo code is not valid.')
      }

      if (couponRes.plans?.length) {
        const matchingPlans = couponRes.plans.some(couponPlan => couponPlan.sku === plan.sku)
        if (!matchingPlans) {
          setCoupon({})
          throw Error('Coupon is not valid with this plan')
        }
      }

      setCoupon(couponRes)
    } catch (error) {
      sendError(error, `Error fetching /api/signup/coupon: ${promoCode}`)
      throw error
    }
  }

  useEffect(() => {
    if (couponCode && plan?.sku) {
      try {
        fetchCoupon(couponCode)
      } catch (err) {
        alerts.error('Error Fetching Coupon')
        sendError(err, 'Error fetching /api/signup/coupon')
      }
    }
  }, [couponCode, plan])

  /**
   * Ask squidward about a user, and say which of the two things happened.
   *
   * A discriminated answer, because the failure value used to be
   * `{ status: 0 }` -- the same shape squidward sends for a user who may not
   * order -- so a dead network routed a customer as though it had been
   * answered. Nothing is written to state unless a real validation came back.
   */
  const validateUser = async ({ email = null, googleId = null }) => {
    const step = navigation.currentStep
    const isGoogle = !!googleId

    let res
    let body
    try {
      res = await fetch('/api/signup/validate', {
        method: 'POST',
        body: JSON.stringify({
          user: {
            ...(email && { email }),
            ...(googleId && { googleId }),
            ...(oauthToken && { oauthToken }),
          },
        }),
      })

      body = await res.json()
    } catch (err) {
      sendError(err, 'Error fetching /api/signup/validate', { step, isGoogle })
      return { kind: VALIDATION_KINDS.requestFailed }
    }

    if (!res.ok || !isRecognizedValidation(body)) {
      sendError(null, 'Sign Up: validate response unrecognized', {
        httpStatus: res.status,
        statusType: typeof body?.status,
        hasUser: !!body?.user,
        hasError: !!body?.error,
        isGoogle,
        step,
      })
      return { kind: VALIDATION_KINDS.requestFailed }
    }

    // Linked to a Google account squidward could not name: the two halves of
    // one answer disagreeing, which routing cannot resolve for itself.
    if (readIsLinkedToGoogle(body) && !body.user?.neatUserId) {
      sendWarning(null, 'Sign Up: validate flags inconsistent', {
        status: body.status,
        hasNeatUserId: !!body.user?.neatUserId,
        isLinkedToGoogle: body.isLinkedToGoogle,
      })
    }

    userValidation.current = body

    // A merge onto the value `user` held when this render's closure was
    // built, so it carries forward whatever the response omits. On the
    // authenticate path that is harmless only because saveStepAuthenticate
    // replaces `user` wholesale a few lines later with a value computed by
    // the retention policy; this merge is the last word only on the
    // validation-error return, where nothing routes anywhere.
    setUser({ ...user, ...body.user })
    setIsLinkedToGoogle(readIsLinkedToGoogle(body))
    return { kind: VALIDATION_KINDS.valid, validation: body }
  }

  const handleRedirectToApp = () => {
    applyTransition(PSEUDO_STATES.global, OUTCOMES.redirectToApp, { effects: { ssoUrl: order?.ssoUrl } })
  }

  useEffect(() => {
    if (!isReady || pathname !== '/t') return

    const fetchPlanAsync = async (planSku) => {
      setIsLoading(true)
      try {
        const fetchedPlan = await fetchPlan(planSku)

        if (!exists && fetchedPlan?.sku) {
          // Entering the checkout. The initial step is `authenticate`, which is
          // a starting state rather than a destination, so this is where the
          // event belongs -- and the gate is what keeps a later return to
          // `authenticate` from emitting a second one.
          const { event, state } = enterCheckout(checkoutTelemetry.current)
          checkoutTelemetry.current = state
          if (event) checkoutTelemetry.current.queue.push({ kind: QUEUE_KINDS.started })
        }

        // A checkout with no plan has nothing to validate a user against and
        // nothing to route on; `fetchPlan` has already said so to the customer.
        if (exists && oauthToken && fetchedPlan) {
          const result = await validateUser({})
          if (result.kind === VALIDATION_KINDS.requestFailed) {
            alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
          } else if (result.validation.status === 0) {
            applyTransition(PSEUDO_STATES.bootstrap, OUTCOMES.existingSession)
          } else {
            // Routes on `fetchedPlan`, not on `plan` state, exactly as before.
            //
            // The user override: validateUser's setUser is
            // a scheduled state write, so `user` in this closure is still the
            // pre-update value -- `{}` on this path, because nothing has run in
            // this page's lifetime: `exists && oauthToken` means we have just
            // navigated back from AUTH_URL. Squidward identified the user by
            // oauthToken and answered with neatUserId, firstName and lastName;
            // without this override the table sees an empty user and routes an
            // existing account holder to the create-an-account step.
            applyTransition(PSEUDO_STATES.bootstrap, OUTCOMES.newSession, {
              plan: fetchedPlan,
              user: { ...user, ...result.validation.user },
            })
          }
        }
      } catch (err) {
        sendError(err, 'Sign Up: bootstrap failed', { stage: 'bootstrap', step: navigation.currentStep })
      } finally {
        // The one place the loading screen comes down, so no path out of this
        // effect can leave it up.
        setIsLoading(false)
      }
    }

    let planSku = defaultSku
    if (sku) {
      planSku = sku
    }

    fetchPlanAsync(planSku)
  }, [sku, isReady])

  /**
   * Send what the transitions decided was owed.
   *
   * This is delivery, not the decision: the queue is appended ONLY by a
   * transition, so a step re-entered by a back press queues nothing and
   * re-emits nothing -- the property the old render effect could not hold,
   * because it re-derived the event from the current step on every render.
   *
   * It runs as an effect so the payload reads settled state. A transition
   * routinely fires in the same tick as the `setPlan`, `setUser` or
   * `setCoupon` that describes it, so building the payload at transition time
   * captures the values the transition just replaced. Waiting for `plan?.sku`
   * also preserves the old effect's ordering guard: no step event was ever
   * sent before a plan had loaded, and a plan that never loads sends nothing.
   *
   * No dependency array, because the queue is a ref: appending to it schedules
   * no render and so could never be a dependency. Every path that queues also
   * sets React state in the same tick -- a step destination falls through to
   * `navigateToStep`, and the bootstrap's `Sign Up Started` is followed by
   * `setIsLoading(false)` -- so a commit always follows a queued event and this
   * effect runs on it. Running on every commit is cheaper than the extra commit
   * a state-backed queue spent clearing itself.
   */
  useEffect(() => {
    const { queue } = checkoutTelemetry.current
    if (!queue.length || !plan?.sku) return

    const snapshot = {
      user, plan, coupon, couponCode, utmCampaign, utm, testsShown: getVariantCookies(),
    }
    planTelemetry(queue, snapshot).forEach(({ tracker, args }) => {
      if (tracker === TRACKERS.started) trackSignUpStarted(...args)
      else if (tracker === TRACKERS.outcome) trackSignUpEvent(...args)
      else trackSignUpStep(...args)
    })

    // Emptied in place: same array, same checkout. Discarding a queue wholesale
    // is `clearCheckout`'s job, and it does it by replacing the owning object.
    queue.length = 0
  })

  const handleForgotPassword = useCallback(async () => {
    const { neatUserId } = user
    try {
      setIsRequestingPasswordReset(true)
      const token = await handleExecuteRecaptcha(RECAPTCHA_V3_ACTIONS.SIGNUP_RESET_PASSWORD_REQUEST)
      const userInfo = { ...user, id: neatUserId }
      delete userInfo.neatUserId
      await handleVerifyRecaptchaV3(token, userInfo)

      const res = await fetch('/api/signup/resetPassword', {
        method: 'POST',
        body: JSON.stringify({ neatUserId }),
      })
      const resMessage = await res.json()
      if (res.status > 300) {
        applyTransition(PSEUDO_STATES.global, OUTCOMES.resetFailed, { effects: { errorMessage: 'Error Resetting Password' } })
        sendError(resMessage, 'Error fetching /api/signup/resetPassword')
      } else {
        applyTransition(PSEUDO_STATES.global, OUTCOMES.resetRequested)
      }
    } catch (err) {
      sendError(err, 'Error fetching /api/signup/resetPassword')
      applyTransition(PSEUDO_STATES.global, OUTCOMES.resetFailed, { effects: { errorMessage: 'Error Resetting Password' } })
    } finally {
      setIsRequestingPasswordReset(false)
    }
  }, [user])

  useEffect(() => {
    const el = document.querySelector('html')
    if (el) el.scrollTop = 0
  }, [navigation.currentStep])

  const createLead = async ({ firstName, lastName, email, phone }, step) => {
    try {
      const res = await fetch('/api/lead/create', {
        method: 'POST',
        body: JSON.stringify(
          {
            email: email ?? user.email,
            firstName: firstName ?? user.firstName,
            lastName: lastName ?? user.lastName,
            phone: phone ?? user.phone,
            source: sources.abandonedCart,
          },
        ),
      })

      // Reported, never raised: a lead is a marketing side effect and must not
      // stop the sign-up the customer is in the middle of.
      if (!res.ok) {
        sendWarning(null, 'Sign Up: lead create failed', { httpStatus: res.status, step })
      }
    } catch (err) {
      sendError(err, 'Sign Up: lead create failed', { step })
    }
  }

  const handleOrderResponse = async (res) => {
    const resMessage = await res.json()
    const outcome = classifyOrderResponse(res, resMessage)

    // Only a completed order has somewhere to send the customer, and only a
    // body that classified as one is safe to write to.
    if (outcome === ORDER_OUTCOMES.completed && jwt) {
      resMessage.ssoUrl = appAuthReturnUrl
    }

    setOrder(resMessage)

    const reportFailure = ORDER_FAILURE_REPORTERS[outcome]
    if (reportFailure) {
      reportFailure(null, 'Sign Up: order failed', {
        outcome,
        httpStatus: res.status,
        bodyStatus: resMessage?.status,
        type: resMessage?.type,
        errorCode: resMessage?.error_code,
        sku: plan?.sku,
        step: navigation.currentStep,
      })
    }

    switch (outcome) {
      case ORDER_OUTCOMES.noCcTrialOffered:
        setShowNoCcTrial(true)
        break
      case ORDER_OUTCOMES.validationFailed:
        setShowNoCcTrial(false)
        setOrderError(formatMessage({ id: 'pages.signUp.requestValidationError' }))
        break
      case ORDER_OUTCOMES.tooManyCcAttempts:
        setShowTooManyAttempts(true)
        setOrderError(resMessage.error?.message)
        break
      case ORDER_OUTCOMES.errorGeneric:
        setRecurlyErrors(ORDER_FAILURE_MESSAGE)
        break
      case ORDER_OUTCOMES.errorSpecific:
        setOrderError(resMessage.error?.message)
        break
      case ORDER_OUTCOMES.statusGeneric:
        setRecurlyErrors(ORDER_FAILURE_MESSAGE)
        break
      case ORDER_OUTCOMES.statusSpecific:
        setOrderError(resMessage.message)
        break
      case ORDER_OUTCOMES.sessionExpired:
        setOrderError('Your session has expired.')
        break
      case ORDER_OUTCOMES.serverFailure:
        setRecurlyErrors(ORDER_FAILURE_MESSAGE)
        break
      case ORDER_OUTCOMES.completed: {
        const testsShown = getVariantCookies()
        trackSignUpCompleted(user, {
          sku: plan?.sku,
          coupon: coupon?.code,
          utmCampaign: utm.campaign,
          utmSource: utm.source,
          utmMedium: utm.medium,
          ...(testsShown.length ? { testsShown } : {}),
        })
        alerts.clear()
        applyTransition(PSEUDO_STATES.global, OUTCOMES.orderCompleted)
        break
      }
      // Fails closed: an outcome with no case of its own ends the attempt on
      // the billing step rather than on the success page.
      default:
        setRecurlyErrors(ORDER_FAILURE_MESSAGE)
        sendError(null, 'Sign Up: unrecognized order response', {
          httpStatus: res.status,
          bodyKeys: Object.keys(resMessage ?? {}),
          sku: plan?.sku,
          step: navigation.currentStep,
        })
    }
  }

  const handleCreateNoCcTrialOrder = async () => {
    alerts.clear()

    setIsOrderProcessing(true)
    setIsOrderRequestProcessing(true)

    const testsShown = getVariantCookies()
    let stage = 'request'
    try {
      const res = await fetch('/api/signup/createTrial', {
        method: 'POST',
        headers: { neat: csrfToken },
        body: JSON.stringify({
          user: { ...user, address: { country: billingData.billingCountry, postalCode: billingData.billingZipCode } },
          plan: { sku: backupSkuMap[plan.sku] },
          ...(coupon?.code && { coupon: { code: coupon.code } }),
          source: sources.backupTrial,
          device: { os: userAgent.device?.model, type: userAgent.browser?.name },
          utm: { ...utm, ...(testsShown.length ? { testsShown } : {}) },
        }),
      })
      stage = 'classify'
      await handleOrderResponse(res)
    } catch (err) {
      setOrderError(formatMessage({ id: 'pages.signUp.requestValidationError' }))
      sendError(err, 'Error Creating Order Error No CC Trial Order', { stage })
    } finally {
      setShowNoCcTrial(false)
      setIsOrderProcessing(false)
      setIsOrderRequestProcessing(false)
    }
  }

  const handleCreateOrder = async () => {
    if (isOrderRequestProcessing) return

    alerts.clear()
    setOrderError(null)
    setRecurlyErrors(null)
    setIsOrderRequestProcessing(true)

    // Preparation runs inside the try: the processing flags are already set,
    // and only the finally clears them.
    let stage = 'recaptcha'
    try {
      const recaptchaV3Token = await handleExecuteRecaptcha(RECAPTCHA_V3_ACTIONS.SIGNUP_CREATE_ORDER)
      stage = 'prepare'
      const forceOrderRequestValidationErrorCode = getForceOrderRequestValidationErrorCode()
      const testsShown = getVariantCookies()

      stage = 'request'
      const res = await fetch('/api/signup/create', {
        method: 'POST',
        headers: { neat: csrfToken },
        body: JSON.stringify({
          user: { ...user, address: { country: billingData.billingCountry, postalCode: billingData.billingZipCode } },
          plan,
          ...(coupon?.code && { coupon: { code: coupon.code } }),
          source: plan.isTrial ? sources.trial : sources.checkout,
          paymentMethod: { token: recurlyToken, type: billingData.paymentMethod },
          device: { os: userAgent.device?.model, type: userAgent.browser?.name },
          utm: { ...utm, ...(testsShown.length ? { testsShown } : {}) },
          rId3: recaptchaV3Token,
          ...forceOrderRequestValidationErrorCode?.length > 0 ? { forceOrderRequestValidationErrorCode } : {},
        }),
      })
      stage = 'classify'
      await handleOrderResponse(res)
    } catch (err) {
      setRecurlyErrors(ORDER_FAILURE_MESSAGE)
      sendError(err, 'Error Creating Order', { stage })
    } finally {
      setIsOrderProcessing(false)
      setIsOrderRequestProcessing(false)
      setRecurlyToken(null)
    }
  }

  useEffect(() => {
    if (recurlyToken && isOrderProcessing) {
      handleCreateOrder()
    }
  }, [recurlyToken, isOrderProcessing])

  useEffect(() => {
    if (recurlyErrors && isOrderProcessing) {
      setIsOrderProcessing(false)
      setOrderError(recurlyErrors)
    }
  }, [recurlyErrors, isOrderProcessing])

  const saveStepBilling = async (model) => {
    setBillingData(model)
    // Phone is collected here, but every order payload posts `user` whole, so
    // it has to land on `user` before either order path can fire.
    // The name too: a Google sign-up without a family_name arrives with the
    // backend's `user` placeholder as its surname, and this form is the only
    // place a real one is collected. A real name already on `user` wins.
    setUser((prev) => ({
      ...prev,
      phone: model.phone,
      firstName: prev.firstName || model.billingFirstName,
      lastName: (!prev.lastName || prev.lastName === 'user') ? model.billingLastName : prev.lastName,
    }))
    setRecurlyErrors(null)

    await detectBot(user.email)
    setIsOrderProcessing(true)
  }

  /**
   * The step is reported and ended rather than raised: every caller raises a
   * blocking overlay for the duration, so a throw from here -- an unreadable
   * Google credential, a recaptcha that never resolved -- used to surface as a
   * spinner that never came down. `stage` names how far the attempt got, since
   * nothing else distinguishes the failures once the throw is caught.
   */
  const saveStepAuthenticate = async (userDataForm, googleId) => {
    const step = navigation.currentStep
    let stage = 'decodeCredential'
    try {
      // creating a copy of the formdata because in safari the email value from the form data was sometimes
      // undefined when running validateUser not sure why exactly? for some reason the form was being reset?
      const userData = { ...userDataForm }
      // Read before validateUser, which schedules its own merge onto `user`.
      // Comparing against that merge would compare the new email with itself.
      const previousUser = user

      if (googleId) {
        const { email: googleEmail, family_name: lastName, given_name: firstName } = JSON.parse(googleId.split('.').map(part => Buffer.from(part, 'base64').toString())[1])
        userData.googleId = googleId
        userData.email = googleEmail
        userData.firstName = firstName
        userData.lastName = lastName
      }

      stage = 'botCheck'
      await detectBot(userData.email)

      stage = 'recaptcha'
      const token = await handleExecuteRecaptcha(RECAPTCHA_V3_ACTIONS.SIGNUP_EMAIL)
      await handleVerifyRecaptchaV3(token, userData)

      stage = 'validate'
      const result = await validateUser(userData)
      if (result.kind === VALIDATION_KINDS.requestFailed) {
        alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
        return
      }
      const { validation } = result

      stage = 'route'
      userData.neatUserId = validation.user?.neatUserId
      if (!userData.firstName && validation.user?.firstName) userData.firstName = validation.user.firstName
      if (!userData.lastName && validation.user?.lastName) userData.lastName = validation.user.lastName

      setCanPurchasePlan(validation.status)

      if (userData.neatUserId) {
        sendRollbarStepData('Existing User Authentication', userData)
      } else if (validation.error) {
        sendRollbarStepData(validation.error.message, userData)
        applyTransition(STEPS.authenticate, OUTCOMES.validationError, {
          user: userData,
          validation,
          effects: { errorMessage: validation.error.message ? validation.error.message : formatMessage({ id: 'pages.signUp.unexpectedError' }) },
        })
        return
      } else {
        sendRollbarStepData('New User Authentication', userData)
      }

      await createLead({ email: userData.email }, navigation.currentStep)

      // One call, a conditional VALUE. Keeping this an unconditional write of a
      // whole computed object is what keeps validateUser's merge above from
      // becoming the last word on a changed email.
      setUser(nextUserAfterAuthenticate(previousUser, userData))

      let outcome
      if (userData.googleId) {
        if (userData.neatUserId) {
          // Check if User can purchase plan and is already linked
          outcome = isLinkedAndPurchasable(validation)
            ? OUTCOMES.googleLinkedExisting
            : OUTCOMES.googleUnlinkedExisting
        } else if (isPurchasable(validation)) {
          outcome = OUTCOMES.googleNewPurchasable
        } else {
          outcome = OUTCOMES.googleNewBlocked
        }
      } else if (validationHasNeatUserId(validation)) {
        outcome = OUTCOMES.emailExisting
      } else if (isPurchasable(validation)) {
        outcome = OUTCOMES.emailNewPurchasable
      } else {
        outcome = OUTCOMES.emailNewBlocked
      }

      applyTransition(STEPS.authenticate, outcome, {
        user: userData,
        validation,
        effects: { errorMessage: formatMessage({ id: 'pages.signUp.unexpectedError' }) },
      })
    } catch (err) {
      sendError(err, 'Sign Up: authenticate failed', { stage, isGoogle: !!googleId, step })
      alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
    }
  }

  const saveStepSelectPlan = async (planSku = null) => {
    if (planSku) await fetchPlan(planSku)

    applyTransition(STEPS.selectPlan, OUTCOMES.submitted)
  }

  const requirePassword = () => user.googleId && !isLinkedToGoogle

  const handleAuth = async ({ email, password }, action) => {
    const step = navigation.currentStep
    let res
    let body
    try {
      let rId
      if (action) {
        rId = await handleExecuteRecaptcha(action)
      }
      res = await fetch('/api/signup/auth', {
        method: 'POST',
        body: JSON.stringify({ email, password, rId }),
      })
      body = await res.json()
    } catch (err) {
      sendError(err, 'Error fetching /api/signup/auth', { action, step })
      return { kind: AUTH_KINDS.requestFailed }
    }

    // The route sends a caught error as a 400 whose body serialises to `{}`,
    // which would otherwise read as `authenticated: false`: a wrong password.
    if (!res.ok) {
      sendError(null, 'Sign Up: auth response not ok', { httpStatus: res.status, action, step })
      return { kind: AUTH_KINDS.requestFailed }
    }
    if (!body || typeof body !== 'object' || Array.isArray(body) || typeof body.authenticated !== 'boolean') {
      sendError(null, 'Sign Up: auth response unrecognized', {
        httpStatus: res.status, authenticatedType: typeof body?.authenticated, action, step,
      })
      return { kind: AUTH_KINDS.requestFailed }
    }

    return { kind: AUTH_KINDS.answered, auth: body }
  }

  const saveStepExistingUser = async (model) => {
    const passwordRequired = requirePassword()
    if (passwordRequired) {
      const result = await handleAuth(model, RECAPTCHA_V3_ACTIONS.SIGNUP_EXISTING_USER)
      if (result.kind === AUTH_KINDS.requestFailed) {
        alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
        return
      }
      const { auth: authResponse } = result
      if (authResponse.authenticated) {
        setUser({ ...user, password: model.password })
        applyTransition(
          STEPS.existingUser,
          canPurchasePlan ? OUTCOMES.authenticatedPurchasable : OUTCOMES.authenticatedNotPurchasable,
          { effects: { ssoUrl: authResponse.ssoUrl } },
        )
      } else {
        applyTransition(STEPS.existingUser, OUTCOMES.authFailed, {
          effects: { failedAuthMessage: 'Your login is incorrect. Please try again.' },
        })
      }
    } else if (user.googleId && !canPurchasePlan) {
      applyTransition(STEPS.existingUser, OUTCOMES.googleNotPurchasable)
    } else {
      applyTransition(STEPS.existingUser, OUTCOMES.noAuthPath, {
        effects: { errorMessage: 'There was an error authenticating. Please try again' },
      })
    }
  }

  const saveStepResetPassword = async (model) => {
    const { password } = model
    if (!user.email) {
      applyTransition(STEPS.resetPassword, OUTCOMES.noEmail, {
        effects: { errorMessage: 'Please provide an email or login with Google' },
      })
    } else {
      const authResult = await handleAuth(
        { email: user.email, password: model.password }, RECAPTCHA_V3_ACTIONS.SIGNUP_RESET_PASSWORD_AUTHENTICATE,
      )
      if (authResult.kind === AUTH_KINDS.requestFailed) {
        alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
        return
      }
      const { auth: authResponse } = authResult

      if (authResponse.authenticated) {
        const result = await validateUser(user)
        if (result.kind === VALIDATION_KINDS.requestFailed) {
          alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
          return
        }
        const { validation } = result

        let outcome
        if (isPurchasable(validation)) {
          user.password = password
          setUser(user)
          outcome = OUTCOMES.authenticatedPurchasable
        } else if (validationHasNeatUserId(validation)) {
          outcome = OUTCOMES.authenticatedExisting
        } else {
          // The original if/else-if chain had no final else: this is a no-op.
          outcome = OUTCOMES.authenticatedUnresolved
        }

        applyTransition(STEPS.resetPassword, outcome, {
          validation,
          effects: { ssoUrl: authResponse.ssoUrl },
        })
      } else {
        applyTransition(STEPS.resetPassword, OUTCOMES.authFailed, {
          effects: { errorMessage: 'Authentication Failed' },
        })
      }
    }
  }

  const saveStepUpsell = async (acceptOffer) => {
    if (acceptOffer) {
      await fetchPlan(UPSELL_PLAN)
      await fetchCoupon(UPSELL_COUPON)
    }
    applyTransition(STEPS.upsell, OUTCOMES.submitted)
  }

  const saveStepNewUser = async (userData) => {
    setUser({ ...user, ...userData })

    const token = await handleExecuteRecaptcha(RECAPTCHA_V3_ACTIONS.SIGNUP_PERSONAL_INFO)
    const dataCopy = { ...userData, email: user.email }
    delete dataCopy.password
    await handleVerifyRecaptchaV3(token, dataCopy)
    // The profile form has no email of its own, and `user` in this closure
    // predates the setUser above: `dataCopy` is the one value that holds the
    // address AND the names just typed, with the password already removed.
    await createLead(dataCopy, navigation.currentStep)

    applyTransition(STEPS.newUser, OUTCOMES.submitted)
  }

  /**
   * Return from the profile step to the email step. The only back edge in the
   * flow. What `user` keeps is decided when the email is re-submitted, not when
   * the user leaves -- but the profile form only reaches `user` on submit, so
   * the names typed so far are carried across here, or the re-entered step
   * comes back empty. Only the two name keys are taken, never the password;
   * an undefined value leaves what `user` already holds.
   */
  const goBackToAuthenticate = ({ firstName, lastName } = {}) => {
    const names = {
      ...(firstName !== undefined ? { firstName } : {}),
      ...(lastName !== undefined ? { lastName } : {}),
    }
    if (Object.keys(names).length) setUser((prev) => ({ ...prev, ...names }))
    applyTransition(STEPS.newUser, OUTCOMES.back)
  }

  return (
    <SignUpContext.Provider value={{
      navigation,
      user,
      plan,
      coupon,
      canPurchasePlan,
      formBoxHeight,
      formHolderHeight,
      order,
      isLoading,
      bannerDescription,
      recurlyFormValidation,
      isOrderProcessing,
      billingData,
      recurlyErrors,
      orderError,
      failedAuth,
      isPayPalOpen,
      isRequestingPasswordReset,
      showNoCcTrial,
      showTooManyAttempts,
      setIsPayPalOpen,
      setCoupon,
      fetchCoupon,
      setFormBoxHeight,
      setFormHolderHeight,
      handleForgotPassword,
      setRecurlyFormValidation,
      saveStepBilling,
      setRecurlyToken,
      setRecurlyErrors,
      saveStepAuthenticate,
      saveStepSelectPlan,
      requirePassword,
      saveStepExistingUser,
      saveStepResetPassword,
      saveStepUpsell,
      saveStepNewUser,
      goBackToAuthenticate,
      setIsOrderProcessing,
      setCsrfToken,
      handleCreateNoCcTrialOrder,
      setShowTooManyAttempts,
      handleRedirectToApp,
    }}
    >
      {children}
    </SignUpContext.Provider>
  )
}

SignUpProvider.propTypes = {
  children: PropTypes.node,
}

export default SignUpProvider
